import { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import DashboardLayout from '@/components/layout/DashboardLayout';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { toast } from 'sonner';
import { 
  TestTubes, 
  Plus, 
  Search, 
  Edit,
  Trash2,
  Upload,
  Download,
  FileSpreadsheet,
  RefreshCw,
  Check,
  AlertCircle
} from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const CATEGORIES = [
  { value: 'blood', label: 'Blood' },
  { value: 'urine', label: 'Urine' },
  { value: 'stool', label: 'Stool' },
  { value: 'microbiology', label: 'Microbiology' },
  { value: 'hormones', label: 'Hormones' },
  { value: 'csf', label: 'CSF' },
  { value: 'imaging', label: 'Imaging' },
  { value: 'pathology', label: 'Pathology' },
  { value: 'genetics', label: 'Genetics' },
  { value: 'cardiac', label: 'Cardiac' },
  { value: 'allergy', label: 'Allergy' },
  { value: 'other', label: 'Other' },
];

const AdminTestsPage = () => {
  const [tests, setTests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [categoryFilter, setCategoryFilter] = useState('all');
  const fileInputRef = useRef(null);
  
  // Modal states
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingTest, setEditingTest] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  
  // Upload states
  const [isUploadModalOpen, setIsUploadModalOpen] = useState(false);
  const [uploadResult, setUploadResult] = useState(null);
  const [uploading, setUploading] = useState(false);
  
  const [formData, setFormData] = useState({
    code: '',
    name: '',
    category: 'blood',
    price: 0,
    turnaround_hours: 24,
    sample_type: 'Blood',
    description: '',
    preparation_instructions: ''
  });

  useEffect(() => {
    fetchTests();
  }, []);

  const fetchTests = async () => {
    try {
      const response = await axios.get(`${API}/admin/test-price-list`);
      setTests(response.data);
    } catch (error) {
      console.error('Error fetching tests:', error);
      toast.error('Failed to load tests');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async () => {
    if (!formData.name || !formData.code) {
      toast.error('Test code and name are required');
      return;
    }
    
    setSubmitting(true);
    try {
      if (editingTest) {
        await axios.put(`${API}/admin/tests/${editingTest.id}/price`, {
          price: formData.price
        });
        toast.success('Test price updated successfully');
      } else {
        await axios.post(`${API}/tests`, formData);
        toast.success('Test added successfully');
      }
      setIsModalOpen(false);
      resetForm();
      fetchTests();
    } catch (error) {
      console.error('Error saving test:', error);
      toast.error(error.response?.data?.detail || 'Failed to save test');
    } finally {
      setSubmitting(false);
    }
  };

  const handleDelete = async (testId) => {
    if (!confirm('Are you sure you want to delete this test?')) return;
    
    try {
      await axios.delete(`${API}/tests/${testId}`);
      toast.success('Test deleted successfully');
      fetchTests();
    } catch (error) {
      console.error('Error deleting test:', error);
      toast.error('Failed to delete test');
    }
  };

  const openAddModal = () => {
    resetForm();
    setEditingTest(null);
    setIsModalOpen(true);
  };

  const openEditModal = (test) => {
    setEditingTest(test);
    setFormData({
      code: test.code || '',
      name: test.name || '',
      category: test.category || 'blood',
      price: test.price || 0,
      turnaround_hours: test.turnaround_hours || 24,
      sample_type: test.sample_type || 'Blood',
      description: test.description || '',
      preparation_instructions: test.preparation_instructions || ''
    });
    setIsModalOpen(true);
  };

  const resetForm = () => {
    setFormData({
      code: '',
      name: '',
      category: 'blood',
      price: 0,
      turnaround_hours: 24,
      sample_type: 'Blood',
      description: '',
      preparation_instructions: ''
    });
  };

  const handleFileUpload = async (event) => {
    const file = event.target.files[0];
    if (!file) return;
    
    if (!file.name.endsWith('.csv')) {
      toast.error('Please upload a CSV file');
      return;
    }
    
    setUploading(true);
    setUploadResult(null);
    
    const formData = new FormData();
    formData.append('file', file);
    
    try {
      const response = await axios.post(`${API}/admin/bulk-upload/tests`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      
      setUploadResult(response.data);
      toast.success(`Successfully imported ${response.data.imported} new tests, updated ${response.data.updated || 0} existing tests`);
      fetchTests();
    } catch (error) {
      console.error('Error uploading file:', error);
      toast.error(error.response?.data?.detail || 'Failed to upload file');
      setUploadResult({ status: 'error', error: error.response?.data?.detail || 'Upload failed' });
    } finally {
      setUploading(false);
      // Reset file input
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  };

  const downloadTemplate = async () => {
    try {
      const response = await axios.get(`${API}/admin/tests/download-template`, {
        responseType: 'blob'
      });
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', 'test_price_list_template.csv');
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      
      toast.success('Template downloaded');
    } catch (error) {
      console.error('Error downloading template:', error);
      toast.error('Failed to download template');
    }
  };

  const exportTests = async () => {
    try {
      const response = await axios.get(`${API}/admin/tests/export`, {
        responseType: 'blob'
      });
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `test_price_list_${new Date().toISOString().split('T')[0]}.csv`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      
      toast.success('Test price list exported');
    } catch (error) {
      console.error('Error exporting tests:', error);
      toast.error('Failed to export tests');
    }
  };

  const filteredTests = tests.filter(test => {
    const matchesSearch = 
      test.name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
      test.code?.toLowerCase().includes(searchQuery.toLowerCase());
    const matchesCategory = categoryFilter === 'all' || test.category === categoryFilter;
    return matchesSearch && matchesCategory;
  });

  return (
    <DashboardLayout>
      <div className="space-y-6" data-testid="admin-tests-page">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
          <div>
            <h1 className="text-2xl font-bold text-foreground">Test Price List Management</h1>
            <p className="text-muted-foreground mt-1">Manage lab tests and pricing</p>
          </div>
          <div className="flex gap-2 flex-wrap">
            <Button 
              variant="outline" 
              onClick={() => setIsUploadModalOpen(true)}
              data-testid="bulk-upload-btn"
            >
              <Upload className="w-4 h-4 mr-2" />
              Bulk Upload
            </Button>
            <Button 
              variant="outline" 
              onClick={exportTests}
              data-testid="export-tests-btn"
            >
              <Download className="w-4 h-4 mr-2" />
              Export CSV
            </Button>
            <Button 
              onClick={openAddModal} 
              className="bg-sky-600 hover:bg-sky-700"
              data-testid="add-test-btn"
            >
              <Plus className="w-4 h-4 mr-2" />
              Add Test
            </Button>
          </div>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <div className="card-clinical text-center">
            <p className="text-2xl font-bold text-foreground">{tests.length}</p>
            <p className="text-sm text-muted-foreground">Total Tests</p>
          </div>
          <div className="card-clinical text-center">
            <p className="text-2xl font-bold text-sky-600">{tests.filter(t => t.category === 'blood').length}</p>
            <p className="text-sm text-muted-foreground">Blood Tests</p>
          </div>
          <div className="card-clinical text-center">
            <p className="text-2xl font-bold text-purple-600">{tests.filter(t => t.category === 'hormones').length}</p>
            <p className="text-sm text-muted-foreground">Hormone Tests</p>
          </div>
          <div className="card-clinical text-center">
            <p className="text-2xl font-bold text-emerald-600">{new Set(tests.map(t => t.category)).size}</p>
            <p className="text-sm text-muted-foreground">Categories</p>
          </div>
        </div>

        {/* Filters */}
        <div className="card-clinical">
          <div className="flex flex-col sm:flex-row gap-4">
            <div className="flex-1 relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
              <Input
                placeholder="Search by name or code..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-9"
                data-testid="search-tests-input"
              />
            </div>
            <Select value={categoryFilter} onValueChange={setCategoryFilter}>
              <SelectTrigger className="w-full sm:w-48" data-testid="category-filter">
                <SelectValue placeholder="Filter by category" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Categories</SelectItem>
                {CATEGORIES.map(cat => (
                  <SelectItem key={cat.value} value={cat.value}>{cat.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </div>

        {/* Tests Table */}
        <div className="card-clinical overflow-hidden">
          {loading ? (
            <div className="flex justify-center py-16">
              <div className="spinner" />
            </div>
          ) : filteredTests.length === 0 ? (
            <div className="text-center py-16">
              <TestTubes className="w-12 h-12 text-muted-foreground mx-auto mb-4" />
              <h3 className="text-lg font-semibold text-foreground">No Tests Found</h3>
              <p className="text-muted-foreground mt-1">Start by adding tests or uploading a CSV</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-border">
                    <th className="text-left py-4 px-4 font-medium text-muted-foreground">Code</th>
                    <th className="text-left py-4 px-4 font-medium text-muted-foreground">Test Name</th>
                    <th className="text-left py-4 px-4 font-medium text-muted-foreground">Category</th>
                    <th className="text-right py-4 px-4 font-medium text-muted-foreground">Price (₦)</th>
                    <th className="text-center py-4 px-4 font-medium text-muted-foreground">Turnaround</th>
                    <th className="text-left py-4 px-4 font-medium text-muted-foreground">Sample</th>
                    <th className="text-right py-4 px-4 font-medium text-muted-foreground">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-border">
                  {filteredTests.map((test) => (
                    <tr key={test.id} className="hover:bg-muted/50" data-testid={`test-row-${test.id}`}>
                      <td className="py-4 px-4 font-mono text-xs text-muted-foreground">{test.code}</td>
                      <td className="py-4 px-4">
                        <p className="font-medium text-foreground">{test.name}</p>
                      </td>
                      <td className="py-4 px-4">
                        <Badge variant="outline" className="capitalize">{test.category}</Badge>
                      </td>
                      <td className="py-4 px-4 text-right font-medium text-foreground">
                        ₦{test.price?.toLocaleString('en-US', { minimumFractionDigits: 2 })}
                      </td>
                      <td className="py-4 px-4 text-center text-muted-foreground">
                        {test.turnaround_hours}hrs
                      </td>
                      <td className="py-4 px-4 text-muted-foreground">
                        {test.sample_type}
                      </td>
                      <td className="py-4 px-4 text-right">
                        <div className="flex justify-end gap-2">
                          <Button 
                            variant="ghost" 
                            size="icon"
                            onClick={() => openEditModal(test)}
                            data-testid={`edit-test-${test.id}`}
                          >
                            <Edit className="w-4 h-4" />
                          </Button>
                          <Button 
                            variant="ghost" 
                            size="icon"
                            onClick={() => handleDelete(test.id)}
                            className="text-red-600 hover:text-red-700"
                            data-testid={`delete-test-${test.id}`}
                          >
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>

      {/* Add/Edit Modal */}
      <Dialog open={isModalOpen} onOpenChange={setIsModalOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>{editingTest ? 'Edit Test' : 'Add New Test'}</DialogTitle>
            <DialogDescription>
              {editingTest ? 'Update test details and pricing' : 'Add a new test to the catalog'}
            </DialogDescription>
          </DialogHeader>
          <div className="py-4 space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Test Code *</Label>
                <Input
                  value={formData.code}
                  onChange={(e) => setFormData({...formData, code: e.target.value})}
                  placeholder="e.g., Z00.100"
                  className="mt-1"
                  disabled={!!editingTest}
                  data-testid="test-code-input"
                />
              </div>
              <div>
                <Label>Category</Label>
                <Select value={formData.category} onValueChange={(v) => setFormData({...formData, category: v})}>
                  <SelectTrigger className="mt-1">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {CATEGORIES.map(cat => (
                      <SelectItem key={cat.value} value={cat.value}>{cat.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="col-span-2">
                <Label>Test Name *</Label>
                <Input
                  value={formData.name}
                  onChange={(e) => setFormData({...formData, name: e.target.value})}
                  placeholder="Enter test name"
                  className="mt-1"
                  disabled={!!editingTest}
                  data-testid="test-name-input"
                />
              </div>
              <div>
                <Label>Price (₦)</Label>
                <Input
                  type="number"
                  value={formData.price}
                  onChange={(e) => setFormData({...formData, price: parseFloat(e.target.value) || 0})}
                  className="mt-1"
                  data-testid="test-price-input"
                />
              </div>
              <div>
                <Label>Turnaround (hours)</Label>
                <Input
                  type="number"
                  value={formData.turnaround_hours}
                  onChange={(e) => setFormData({...formData, turnaround_hours: parseInt(e.target.value) || 24})}
                  className="mt-1"
                />
              </div>
              <div className="col-span-2">
                <Label>Sample Type</Label>
                <Input
                  value={formData.sample_type}
                  onChange={(e) => setFormData({...formData, sample_type: e.target.value})}
                  placeholder="e.g., Blood, Urine"
                  className="mt-1"
                />
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setIsModalOpen(false)}>Cancel</Button>
            <Button 
              onClick={handleSubmit} 
              disabled={submitting}
              className="bg-sky-600 hover:bg-sky-700"
              data-testid="save-test-btn"
            >
              {submitting ? 'Saving...' : editingTest ? 'Update Test' : 'Add Test'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Bulk Upload Modal */}
      <Dialog open={isUploadModalOpen} onOpenChange={setIsUploadModalOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <FileSpreadsheet className="w-5 h-5 text-sky-600" />
              Bulk Upload Tests
            </DialogTitle>
            <DialogDescription>
              Upload a CSV file to bulk import or update test prices
            </DialogDescription>
          </DialogHeader>
          <div className="py-4 space-y-4">
            {/* Instructions */}
            <div className="p-4 bg-sky-50 dark:bg-sky-900/30 rounded-lg">
              <h4 className="font-medium text-sky-800 dark:text-sky-300 mb-2">CSV Format</h4>
              <p className="text-sm text-sky-700 dark:text-sky-400">
                Your CSV should have these columns:
              </p>
              <code className="block mt-2 text-xs bg-white dark:bg-slate-800 p-2 rounded text-foreground">
                Code, Name, Category, Price, Turnaround (hrs), Sample Type
              </code>
              <p className="text-xs text-sky-600 dark:text-sky-400 mt-2">
                • Existing tests will be updated by code<br/>
                • New codes will create new tests<br/>
                • Prices can include ₦ symbol and commas
              </p>
            </div>
            
            {/* Download Template */}
            <Button 
              variant="outline" 
              className="w-full"
              onClick={downloadTemplate}
              data-testid="download-template-btn"
            >
              <Download className="w-4 h-4 mr-2" />
              Download Template
            </Button>
            
            {/* File Upload */}
            <div className="border-2 border-dashed border-border rounded-lg p-6 text-center">
              <input
                ref={fileInputRef}
                type="file"
                accept=".csv"
                onChange={handleFileUpload}
                className="hidden"
                id="csv-upload"
                data-testid="csv-file-input"
              />
              <label 
                htmlFor="csv-upload"
                className="cursor-pointer flex flex-col items-center"
              >
                {uploading ? (
                  <>
                    <RefreshCw className="w-10 h-10 text-sky-600 animate-spin mb-3" />
                    <p className="text-foreground font-medium">Uploading...</p>
                  </>
                ) : (
                  <>
                    <Upload className="w-10 h-10 text-muted-foreground mb-3" />
                    <p className="text-foreground font-medium">Click to upload CSV file</p>
                    <p className="text-sm text-muted-foreground mt-1">or drag and drop</p>
                  </>
                )}
              </label>
            </div>
            
            {/* Upload Result */}
            {uploadResult && (
              <div className={`p-4 rounded-lg ${
                uploadResult.status === 'success' 
                  ? 'bg-emerald-50 dark:bg-emerald-900/30 border border-emerald-200 dark:border-emerald-800' 
                  : 'bg-red-50 dark:bg-red-900/30 border border-red-200 dark:border-red-800'
              }`}>
                {uploadResult.status === 'success' ? (
                  <>
                    <div className="flex items-center gap-2 mb-2">
                      <Check className="w-5 h-5 text-emerald-600" />
                      <span className="font-medium text-emerald-800 dark:text-emerald-300">Upload Successful</span>
                    </div>
                    <p className="text-sm text-emerald-700 dark:text-emerald-400">
                      • {uploadResult.imported} new tests imported<br/>
                      • {uploadResult.updated || 0} existing tests updated
                    </p>
                    {uploadResult.errors && uploadResult.errors.length > 0 && (
                      <div className="mt-2 text-sm text-amber-700 dark:text-amber-400">
                        <p className="font-medium">Warnings:</p>
                        <ul className="list-disc list-inside">
                          {uploadResult.errors.slice(0, 3).map((err, i) => (
                            <li key={i}>{err}</li>
                          ))}
                          {uploadResult.errors.length > 3 && (
                            <li>...and {uploadResult.errors.length - 3} more</li>
                          )}
                        </ul>
                      </div>
                    )}
                  </>
                ) : (
                  <>
                    <div className="flex items-center gap-2">
                      <AlertCircle className="w-5 h-5 text-red-600" />
                      <span className="font-medium text-red-800 dark:text-red-300">Upload Failed</span>
                    </div>
                    <p className="text-sm text-red-700 dark:text-red-400 mt-1">{uploadResult.error}</p>
                  </>
                )}
              </div>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => {
              setIsUploadModalOpen(false);
              setUploadResult(null);
            }}>
              Close
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </DashboardLayout>
  );
};

export default AdminTestsPage;
